import { ApolloCache, gql } from '@apollo/client';

// TA-10153: reactions from other users are delivered via the messageUpdate
// subscription, but a reaction changes an EXISTING message — so when the
// WebSocket drops an event, nothing ever re-reads it: the heartbeat restart
// only re-subscribes, and fetchNewerMessages only backfills messages newer
// than the cache. This module re-reads a reaction-lite page of the open chat
// and writes the reaction field of each message directly into the cache.
//
// NOTE: defined with gql here instead of a src/graphql/*.graphql document
// because codegen cannot run offline in this environment (remote
// CODEGEN_SCHEMA_URL unreachable, checked-in local-schema.graphql stale).
// Safe to migrate to a generated hook at the next remote-schema codegen.

export const MESSAGE_REACTIONS_BACKFILL_FRAGMENT = gql`
  fragment MessageReactionsBackfill on Message {
    id
    clientId
    reaction {
      ... on MessageReaction {
        id
        reaction
        count
        user {
          id
          name
        }
      }
    }
  }
`;

// Fetch with fetchPolicy 'no-cache': the result must not route through the
// Query.listMessages merge policy, which would reset pageInfo to page-1 and
// disturb a user paginating older messages (the reason refetch() was removed
// from the heartbeat). Nodes are written individually via
// backfillMessageReactions instead.
export const LIST_MESSAGES_REACTIONS_QUERY = gql`
  query listMessagesReactionsBackfill($chatId: ID!, $first: Int) {
    listMessages(chatId: $chatId, first: $first) {
      edges {
        node {
          ...MessageReactionsBackfill
        }
      }
    }
  }
  ${MESSAGE_REACTIONS_BACKFILL_FRAGMENT}
`;

type ReactionUser = { id: string; name?: string | null };
type MessageReactionLite = {
  id: string;
  reaction: string;
  count: number;
  user?: ReactionUser[] | null;
  __typename?: string;
};
export type ReactionBackfillNode = {
  id: string;
  clientId?: string | null;
  reaction?: MessageReactionLite[] | null;
  __typename?: string;
};
export type ReactionBackfillEdge = {
  node?: ReactionBackfillNode | null;
} | null;

/**
 * Write the reaction field of each fetched message into its normalized cache
 * entity. Message entities are keyed by clientId (initializeCache), so nodes
 * without one cannot be identified and are skipped. Returns how many
 * messages were written.
 */
export const backfillMessageReactions = (
  cache: ApolloCache<unknown>,
  edges: ReactionBackfillEdge[] | null | undefined
): number => {
  let written = 0;
  (edges ?? []).forEach((edge) => {
    const node = edge?.node;
    if (!node?.clientId) return;
    const id = cache.identify({
      __typename: 'Message',
      clientId: node.clientId,
    });
    if (!id) return;
    cache.writeFragment({
      id,
      fragment: MESSAGE_REACTIONS_BACKFILL_FRAGMENT,
      fragmentName: 'MessageReactionsBackfill',
      data: {
        __typename: 'Message',
        id: node.id,
        clientId: node.clientId,
        reaction: node.reaction ?? [],
      },
    });
    written += 1;
  });
  return written;
};
